import { useCallback, useEffect, useRef, useState } from "react";
import {
  Camera,
  Upload,
  Link as LinkIcon,
  Copy,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ScanBarcode,
  StopCircle,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  decodeFromFile,
  decodeFromImageUrl,
  formatLabel,
  startCameraScan,
  type CameraController,
  type DecodeResult,
} from "@/lib/zxing";

export type ScannerAppProps = {
  /** If set, the result panel warns when the decoded label doesn't match. e.g. "Data Matrix". */
  expectedFormat?: string;
  /** Hint shown in the empty Result panel. */
  emptyHint?: string;
  /** Label for the camera tab help line. */
  cameraHint?: string;
};

type Status =
  | { kind: "idle" }
  | { kind: "loading"; label: string }
  | { kind: "scanning" }
  | { kind: "success"; result: DecodeResult }
  | { kind: "error"; message: string };

export function ScannerApp({ expectedFormat, emptyHint, cameraHint }: ScannerAppProps) {
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [tab, setTab] = useState<"camera" | "upload" | "url">("camera");
  const [imageUrl, setImageUrl] = useState("");

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controllerRef = useRef<CameraController | null>(null);
  // Monotonic token bumped on every stop. `startCameraScan` is async
  // (getUserMedia); if a stop/unmount fires while the stream is still
  // negotiating, the captured token goes stale and the resolved controller is
  // released immediately instead of leaking a live camera (hardware light
  // stuck on).
  const startIdRef = useRef(0);

  const stopCamera = useCallback(() => {
    startIdRef.current += 1;
    controllerRef.current?.stop();
    controllerRef.current = null;
  }, []);

  useEffect(() => {
    if (tab !== "camera") stopCamera();
    return () => stopCamera();
  }, [tab, stopCamera]);

  const handleStartCamera = async () => {
    if (!videoRef.current) return;
    stopCamera();
    const startId = startIdRef.current;
    setStatus({ kind: "loading", label: "Requesting camera…" });
    try {
      const ctrl = await startCameraScan(
        videoRef.current,
        (r) => {
          stopCamera();
          setStatus({ kind: "success", result: r });
        },
        (err) => {
          stopCamera();
          setStatus({ kind: "error", message: err.message });
        },
      );
      if (startId !== startIdRef.current) {
        // Stopped/unmounted while starting — release the stream, don't store it.
        ctrl.stop();
        return;
      }
      controllerRef.current = ctrl;
      setStatus({ kind: "scanning" });
    } catch (err) {
      if (startId !== startIdRef.current) return; // stale failure — ignore
      const msg =
        err instanceof Error
          ? err.name === "NotAllowedError"
            ? "Camera permission denied. Allow camera access and try again."
            : err.message
          : "Could not start the camera.";
      setStatus({ kind: "error", message: msg });
    }
  };

  const handleFile = async (file: File) => {
    setStatus({ kind: "loading", label: "Decoding image…" });
    try {
      const result = await decodeFromFile(file);
      setStatus({ kind: "success", result });
    } catch {
      setStatus({
        kind: "error",
        message:
          "No barcode found in that image. Try a clearer, higher-contrast photo where the barcode fills most of the frame.",
      });
    }
  };

  const handleUrl = async () => {
    const url = imageUrl.trim();
    if (!url) return;
    setStatus({ kind: "loading", label: "Loading image…" });
    try {
      const result = await decodeFromImageUrl(url);
      setStatus({ kind: "success", result });
    } catch (err) {
      const errMsg = err instanceof Error ? err.message.toLowerCase() : "";
      const isCors = errMsg.includes("cors") || errMsg.includes("load image");
      const msg = isCors
        ? "That host blocks cross-origin image access, so the barcode can't be read directly. Download the image and use the Image tab instead."
        : "Couldn't decode an image from that URL. Check the link or try uploading the file directly.";
      setStatus({ kind: "error", message: msg });
    }
  };

  const reset = () => {
    stopCamera();
    setStatus({ kind: "idle" });
    setImageUrl("");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="camera">
                <Camera className="mr-2 h-4 w-4" />
                Camera
              </TabsTrigger>
              <TabsTrigger value="upload">
                <Upload className="mr-2 h-4 w-4" />
                Image
              </TabsTrigger>
              <TabsTrigger value="url">
                <LinkIcon className="mr-2 h-4 w-4" />
                URL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="camera" className="mt-5">
              <CameraPanel
                videoRef={videoRef}
                status={status}
                hint={cameraHint}
                onStart={handleStartCamera}
                onStop={() => {
                  stopCamera();
                  setStatus({ kind: "idle" });
                }}
              />
            </TabsContent>

            <TabsContent value="upload" className="mt-5">
              <UploadPanel onFile={handleFile} status={status} />
            </TabsContent>

            <TabsContent value="url" className="mt-5">
              <UrlPanel
                value={imageUrl}
                onChange={setImageUrl}
                onSubmit={handleUrl}
                status={status}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ResultPanel
        status={status}
        onReset={reset}
        expectedFormat={expectedFormat}
        emptyHint={emptyHint}
      />
    </div>
  );
}

function CameraPanel({
  videoRef,
  status,
  hint,
  onStart,
  onStop,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  status: Status;
  hint?: string;
  onStart: () => void;
  onStop: () => void;
}) {
  const scanning = status.kind === "scanning";
  const loading = status.kind === "loading";
  return (
    <div>
      <div className="relative overflow-hidden rounded-lg border border-border bg-muted">
        <video
          ref={videoRef}
          className="aspect-video w-full bg-black object-cover"
          playsInline
          muted
        />
        {!scanning && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/80 p-6 text-center">
            <Camera className="h-8 w-8 text-muted-foreground" aria-hidden />
            <div className="text-sm text-muted-foreground">
              {hint ?? "Point your camera at a barcode. We'll decode it automatically."}
            </div>
          </div>
        )}
        {scanning && (
          <div className="pointer-events-none absolute inset-x-6 top-1/2 h-0.5 -translate-y-1/2 bg-primary/80 shadow-[0_0_12px_var(--color-primary)]" />
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {!scanning ? (
          <Button onClick={onStart} disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Camera className="mr-2 h-4 w-4" />
            )}
            Start camera
          </Button>
        ) : (
          <Button onClick={onStop} variant="outline">
            <StopCircle className="mr-2 h-4 w-4" /> Stop camera
          </Button>
        )}
        <p className="self-center text-xs text-muted-foreground">
          Camera access requires HTTPS and a one-time permission.
        </p>
      </div>
    </div>
  );
}

function UploadPanel({ onFile, status }: { onFile: (file: File) => void; status: Status }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const loading = status.kind === "loading";

  return (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) onFile(file);
        }}
        className={`flex aspect-video w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed text-center transition-colors ${
          dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/40 hover:bg-muted"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = "";
          }}
        />
        <Upload className="h-8 w-8 text-muted-foreground" aria-hidden />
        <div className="text-sm">
          <span className="font-medium">Drop an image</span>
          <span className="text-muted-foreground"> or click to browse</span>
        </div>
        <div className="text-xs text-muted-foreground">PNG, JPG, WEBP, BMP — up to ~10 MB</div>
      </label>
      <div className="mt-4 flex items-center gap-2">
        <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Upload className="mr-2 h-4 w-4" />
          )}
          Choose file
        </Button>
        <p className="text-xs text-muted-foreground">
          Decoded locally — your image never leaves your device.
        </p>
      </div>
    </div>
  );
}

function UrlPanel({
  value,
  onChange,
  onSubmit,
  status,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  status: Status;
}) {
  const loading = status.kind === "loading";
  return (
    <div>
      <label className="text-sm font-medium" htmlFor="img-url">
        Image URL
      </label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <Input
          id="img-url"
          type="url"
          placeholder="https://example.com/barcode.png"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
        />
        <Button onClick={onSubmit} disabled={!value.trim() || loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ScanBarcode className="mr-2 h-4 w-4" />
          )}
          Decode URL
        </Button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        The image is loaded straight from the source — if the host blocks cross-origin requests,
        download it and use the upload tab instead.
      </p>
      {value && (
        <div className="mt-4 overflow-hidden rounded-lg border border-border bg-muted/40">
          <img
            src={value}
            alt="Preview of the image to decode"
            className="mx-auto max-h-64 object-contain"
            onError={() => {
              /* preview error swallowed */
            }}
          />
        </div>
      )}
    </div>
  );
}

function ResultPanel({
  status,
  onReset,
  expectedFormat,
  emptyHint,
}: {
  status: Status;
  onReset: () => void;
  expectedFormat?: string;
  emptyHint?: string;
}) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Result
          </p>
          <Button variant="ghost" size="sm" onClick={onReset}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Reset
          </Button>
        </div>

        <div className="mt-4 flex-1">
          {status.kind === "idle" && (
            <EmptyState>
              {emptyHint ??
                "Decode a barcode from your camera, an uploaded image, or an image URL — the result will appear here."}
            </EmptyState>
          )}

          {status.kind === "loading" && (
            <CenterState>
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div className="text-sm text-muted-foreground">{status.label}</div>
            </CenterState>
          )}

          {status.kind === "scanning" && (
            <CenterState>
              <div className="flex items-center gap-2 text-sm text-primary">
                <Loader2 className="h-4 w-4 animate-spin" />
                Scanning…
              </div>
              <div className="text-xs text-muted-foreground">
                Hold steady — the result will appear automatically.
              </div>
            </CenterState>
          )}

          {status.kind === "error" && (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Couldn't decode</span>
              </div>
              <p className="text-sm text-muted-foreground">{status.message}</p>
            </div>
          )}

          {status.kind === "success" && (
            <SuccessView
              result={status.result}
              expectedFormat={expectedFormat}
              onCopy={copy}
              onReset={onReset}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SuccessView({
  result,
  expectedFormat,
  onCopy,
  onReset,
}: {
  result: DecodeResult;
  expectedFormat?: string;
  onCopy: (s: string) => void;
  onReset: () => void;
}) {
  const label = formatLabel(result.format);
  const mismatch = expectedFormat && label !== expectedFormat;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="h-4 w-4" />
        <span className="text-sm font-medium">Decoded</span>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Format</div>
        <Badge variant="secondary" className="mt-1">
          {label}
        </Badge>
      </div>
      {mismatch && (
        <div className="flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Decoded as <strong>{label}</strong>, not <strong>{expectedFormat}</strong>. The value is
            still valid — use the matching tool for richer parsing.
          </span>
        </div>
      )}
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Value</div>
        <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md border border-border bg-muted/40 p-3 font-mono text-sm">
          {result.text}
        </pre>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => onCopy(result.text)} size="sm">
          <Copy className="mr-2 h-3.5 w-3.5" /> Copy value
        </Button>
        <Button onClick={onReset} size="sm" variant="outline">
          Scan another
        </Button>
      </div>
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
      <ScanBarcode className="h-7 w-7 text-muted-foreground" aria-hidden />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function CenterState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted/30 p-6 text-center">
      {children}
    </div>
  );
}
